import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Popper,
  TextField,
  Typography,
  PaginationItem,
} from '@mui/material';
import MuiPagination from '@mui/material/Pagination';
import { styled } from '@mui/material/styles';
import {
  DataGridPro,
  gridPageCountSelector,
  GridPagination,
  useGridSelector,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { getUsersData, getUsersFilters } from '../../../../packages/common/api';
import Page from '../../../../packages/common/shared-ui/src/components/Page';
import ChevronIcon from '../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import ClearIcon from '../../../../packages/common/shared-ui/src/icons/ClearIcon';
import PaginationNextButtonIcon from '../../../../packages/common/shared-ui/src/icons/PaginationNextButtonIcon';
import PaginationPreviousButtonIcon from '../../../../packages/common/shared-ui/src/icons/PaginationPreviousButtonIcon';
import SortIcon from '../../../../packages/common/shared-ui/src/icons/SortIcon';
import SubmitIcon from '../../../../packages/common/shared-ui/src/icons/SubmitIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import { getTranslatedText } from '../../../../packages/common/utils/getTranslatedText';

// Original class: filterSelect
const StyledFilterSelect = styled(Autocomplete)(theme => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    backgroundColor: '#FFFFFF',
    width: '100%',
    fontSize: '14px',
    lineHeight: '20px',
    border: '1px solid transparent',
    '&:hover': {
      border: '1px solid transparent',
      backgroundColor: '#FFFFFF',
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      padding: 0,
      border: 'none',
    },
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '14px',
      lineHeight: '20px',
      color: '#7174AC',
      padding: '0',
    },
  },

  '&.MuiAutocomplete-hasClearIcon': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderColor: '#3448FF',
      '&:hover': {
        borderColor: '#3448FF',
      },
    },
  },

  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    top: '-5px',
    zIndex: 99,
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '& + .MuiAutocomplete-popper': {
    minWidth: '180px!important',
    width: 'auto!important',
    maxWidth: '300px!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      borderTop: '1px solid #E4E4EF',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: ' nowrap',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '&:hover': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
// Original class: SpentTimeTableRoot
const StyledSpentTimeTableRoot = styled(DataGridPro)(({ theme }) => ({
  '&.MuiDataGrid-root': {
    marginBottom: theme.spacing(4),
    border: 0,
    '& .MuiDataGrid-main': {
      backgroundColor: '#ffffff',
      borderRadius: theme.spacing(4),
    },
    '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor':
      {
        borderRight: 0,
        boxShadow: '1px 1px #E4E4EF',
        borderColor: 'transparent',
        '& .MuiDataGrid-columnHeaderTitleContainer': {
          whiteSpace: 'initial',
        },
      },
    '& .MuiDataGrid-row .MuiDataGrid-cell:last-child': {
      borderRight: 0,
    },
    '& .MuiDataGrid-row.disabled': {
      color: '#7174AC',
    },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-row.Mui-selected.Mui-hovered': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-main .MuiDataGrid-iconButtonContainer .MuiButtonBase-root':
      {
        marginLeft: theme.spacing(2),
      },
    '& .MuiDataGrid-columnSeparator': {
      '& .MuiSvgIcon-root': {
        width: '24px',
      },
    },
    '& .MuiDataGrid-cell': {
      '&.tableCell': {
        borderColor: 'transparent',
        boxShadow: '1px 1px #E4E4EF',
        '&:last-child': {
          boxShadow: 0,
        },
        '& .MuiCheckbox-root': {
          padding: 0,
        },
        '&.textCenter': {
          textAlign: 'center',
          justifyContent: 'center',
        },
      },
      '& a': {
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
  },

  '& .MuiDataGrid-footerContainer': {
    marginTop: '20px',
    minHeight: 'auto',
    border: 'none',
    justifyContent: 'flex-start',
    '& .MuiToolbar-root.MuiTablePagination-toolbar': {
      minHeight: 'auto',
      padding: 0,
      '& .MuiPagination-root': {
        marginLeft: 0,
        '& li': {
          fontSize: '16px',
          lineHeight: '20px',
        },
        '& li + li': {
          marginLeft: '2px',
        },
        '& .MuiButtonBase-root.MuiPaginationItem-root': {
          color: '#41424E',
          fontSize: '16px',
          lineHeight: '20px',
          borderRadius: '4px',
          minWidth: '28px',
          height: '28px',
          margin: 0,
          '&.Mui-selected': {
            backgroundColor: '#6270D0',
            color: '#FAFAFC',
            '&:hover': {
              color: '#FAFAFC',
              backgroundColor: '#6270D0',
            },
          },
          '& .MuiSvgIcon-root.MuiPaginationItem-icon': {
            width: 'auto',
            height: 'auto',
          },
          '&:hover': {
            color: '#6270D0',
            backgroundColor: 'transparent',
          },
        },
        '& .MuiPaginationItem-ellipsis': {
          fontSize: '16px',
          lineHeight: '20px',
          color: '#41424E',
          padding: 0,
          margin: 0,
          minWidth: ' 28px',
        },
      },
    },
  },
}));

const UsersList = () => {
  const getLocalString = useTranslation();
  const pageTitle = getLocalString('AdminUsers', 'title');
  const [usersColumns, setUsersColumns] = useState([]);
  const [usersRows, setUsersRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [allFilters, setAllFilters] = useState([]);
  useEffect(() => {
    let ignore = false;
    async function getFilters() {
      const { data: filters } = await getUsersFilters();
      const { filters: data } = filters;
      const updatedArray = {
        ...data[1],
        values: data[1].values.map(item => {
          return { ...item, valueName: item.valueName };
        }),
      };
      if (!ignore) {
        setAllFilters([...data.slice(0, 1), updatedArray, ...data.slice(2)]);
      }
    }
    getFilters().then(() => {});
    return () => {
      ignore = true;
    };
  }, []);
  useEffect(() => {
    setLoading(true);
    const filters = selectedFilter
      ? { filters: Object.values(selectedFilter).join('$$') }
      : null;
    try {
      getUsersData(filters).then(response => {
        setUsersRows(response.data.rows.filter(item => item.rowId !== 'total'));
        setUsersColumns(response.data.fields);
        setLoading(false);
      });
    } catch (error) {
      console.error(error, 'ERROR IN GET USERS');
    }
  }, [selectedFilter]);

  const cellRenderer = useCallback(({ props, column }) => {
    const linkFields = ['user', 'email'];
    if (linkFields.includes(column.field)) {
      return (
        <Link
          to={`${window.location.origin}/administration/users/${props.row.id}`}
        >
          {props.formattedValue}
        </Link>
      );
    }
    if (column.field === 'admin') {
      // TODO: добавить boolean на бэке

      return (
        props.value === 'Да' && <SubmitIcon sx={{ width: '16px' }}></SubmitIcon>
      );
    }
  }, []);

  const formattedUsersColumns = useCallback(() => {
    const smallFields = ['date', 'bool'];
    return usersColumns.map(column => {
      return {
        field: column.fieldId,
        headerName: column.fieldName,
        renderHeader: props => (
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
            }}
          >
            {props.colDef.headerName}
          </Typography>
        ),
        disableExport: false,
        position: column.position,
        flex: smallFields.includes(column.fieldType) ? 0 : 1,
        width: smallFields.includes(column.fieldType) && 150,
        renderCell: props => cellRenderer({ props, column }),
        cellClassName: props =>
          props.field === 'users.admin' ? 'tableCell textCenter' : 'tableCell',
      };
    });
  }, [cellRenderer, usersColumns]);

  const usersColumnsValue = formattedUsersColumns();

  const formattedUsersRows = useCallback(
    () =>
      usersRows.reduce((acc, row) => {
        const newCols = row.columns.reduce((columnAcc, _, i) => {
          return {
            ...columnAcc,
            [row.columns[i].fieldId]: row.columns[i].value,
          };
        }, {});
        return [
          ...acc,
          { ...newCols, id: row.rowId, active: row.services.active },
        ];
      }, []),
    [usersRows],
  );

  const usersRowsValue = formattedUsersRows();

  const handleSelect = useCallback((event, value, type) => {
    if (!value) {
      setSelectedFilter(prev => {
        const state = { ...prev };
        delete state[type];
        if (Object.values(state).length === 0) {
          return null;
        }
        return state;
      });
      return;
    }
    const text = `${type};eq;${value.valueId}`;
    setSelectedFilter(prev => ({ ...prev, [type]: text }));
  }, []);

  const FilterSelect = useCallback(
    ({ type, label, items }) => {
      return (
        <StyledFilterSelect
          onChange={(event, newValue) => handleSelect(event, newValue, type)}
          disablePortal
          options={items}
          sx={{ width: 180 }}
          clearIcon={<ClearIcon></ClearIcon>}
          renderOption={(props, option) => {
            return (
              <MenuItem {...props} key={option.valueId} value={option.valueId}>
                {option.valueName}
              </MenuItem>
            );
          }}
          getOptionLabel={option => option.valueName}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              placeholder={getLocalString('labels', 'input')}
            />
          )}
          popupIcon={<ChevronIcon direction='down'></ChevronIcon>}
          slots={{
            popper: props => (
              <Popper {...props} placement='bottom-start'></Popper>
            ),
          }}
        />
      );
    },
    [getLocalString, handleSelect],
  );
  function Pagination({ page, onPageChange, className }) {
    const apiRef = useGridApiContext();
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <MuiPagination
        color='primary'
        className={className}
        count={pageCount}
        siblingCount={1}
        boundaryCount={0}
        page={page + 1}
        renderItem={item => {
          if (item.type === 'next') {
            return (
              !item.disabled && (
                <Box>
                  {getLocalString('labels', 'next')}
                  <PaginationItem
                    {...item}
                    slots={{
                      next: PaginationNextButtonIcon,
                    }}
                  ></PaginationItem>
                </Box>
              )
            );
          } else if (item.type === 'previous') {
            return (
              !item.disabled && (
                <Box>
                  <PaginationItem
                    {...item}
                    slots={{
                      previous: PaginationPreviousButtonIcon,
                    }}
                  ></PaginationItem>
                  {getLocalString('labels', 'previous')}
                </Box>
              )
            );
          } else {
            return <PaginationItem {...item}></PaginationItem>;
          }
        }}
        onChange={(event, newPage) => {
          onPageChange(event, newPage - 1);
        }}
      />
    );
  }
  function CustomPagination(props) {
    return (
      <GridPagination
        rowsPerPage={20}
        labelDisplayedRows={() => ''}
        rowsPerPageOptions={[]}
        ActionsComponent={Pagination}
        {...props}
      />
    );
  }
  return (
    <Page title={pageTitle} width='100%'>
      <Grid container spacing={5} sx={{ mb: '30px' }}>
        <Grid>
          <Typography
            variant='h1'
            sx={{
              m: 0,
              padding: '10px 0',
            }}
          >
            {pageTitle}
          </Typography>
        </Grid>
        {allFilters &&
          allFilters.map(
            (filter, index) =>
              filter.field !== 'group' && (
                <Grid key={index}>
                  <FilterSelect
                    items={filter.values}
                    type={`${filter.instance}.${filter.field}`}
                    label={filter.fieldName}
                  ></FilterSelect>
                </Grid>
              ),
          )}
      </Grid>
      {loading ? (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '70px',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      ) : (
        <StyledSpentTimeTableRoot
          localeText={{
            noRowsLabel: getLocalString('labels', 'noData'),
          }}
          pagination
          autoHeight
          disableRowSelectionOnClick
          disableColumnMenu
          columns={usersColumnsValue}
          rows={usersRowsValue}
          getRowId={item => item.id}
          getRowClassName={params => !params.row.active && 'disabled'}
          isRowSelectable={params => !params.row.groupName}
          pageSize={10}
          slots={{
            pagination: CustomPagination,
            columnUnsortedIcon: () => (
              <SortIcon width='20' height='20' viewBox='0 0 20 20' />
            ),
            columnSortedAscendingIcon: () => (
              <SortIcon
                direction='up'
                width='20'
                height='20'
                viewBox='0 0 20 20'
              />
            ),
            columnSortedDescendingIcon: () => (
              <SortIcon
                direction='down'
                width='20'
                height='20'
                viewBox='0 0 20 20'
              />
            ),
          }}
          rowHeight={40}
          sx={{
            '& .MuiTablePagination-spacer': {
              display: 'none',
            },
          }}
        />
      )}
    </Page>
  );
};

export default UsersList;
